import Breadcrumbs, { BreadcrumbsItem } from '@atlaskit/breadcrumbs';
import Button from '@atlaskit/button/new';
import Spinner from '@atlaskit/spinner';
import TextField from '@atlaskit/textfield';
import PageHeader from '@atlaskit/page-header';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
  ApiRequestError,
  demandPartnerServices,
  type DpCompanyDetails,
  type DpPocDetails
} from '@shared/api';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { setDemandPartnerProfile } from '@shared/store/partnerSlice';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

type EditableFieldValue = string | number | undefined;

type DetailField<TValue extends object> = {
  key: keyof TValue;
  label: string;
  type?: 'text' | 'number' | 'email';
};

interface DetailSectionCardProps<TValue extends object> {
  title: string;
  value?: TValue;
  fallbackValue?: TValue;
  fields: DetailField<TValue>[];
  isSaving: boolean;
  onSave: (value: TValue) => void;
  helperMessage?: string;
  errorMessage?: string;
}

const cardStyles = {
  background: token('color.background.input'),
  border: `1px solid ${token('color.border')}`,
  borderRadius: '18px',
  padding: token('space.300')
} as const;

const dividerStyles = {
  background: token('color.border'),
  height: '1px',
  width: '100%'
} as const;

const formatAuditDate = (value?: string) =>
  value
    ? new Intl.DateTimeFormat('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(new Date(value))
    : 'Not available';

function DetailSectionCard<TValue extends object>({
  title,
  value,
  fallbackValue,
  fields,
  isSaving,
  onSave,
  helperMessage,
  errorMessage
}: DetailSectionCardProps<TValue>) {
  const resolvedValue = value ?? fallbackValue;
  const [isEditing, setIsEditing] = useState(() => !value && Boolean(fallbackValue));
  const [draft, setDraft] = useState<TValue | undefined>(resolvedValue);

  useEffect(() => {
    setDraft(resolvedValue);
    setIsEditing(!value && Boolean(fallbackValue));
  }, [fallbackValue, resolvedValue, value]);

  if (!resolvedValue || !draft) {
    return (
      <Box style={cardStyles}>
        <Stack space="space.150">
          <Text size="large" weight="medium">
            {title}
          </Text>
          {errorMessage ? (
            <Text color="color.text.danger">{errorMessage}</Text>
          ) : (
            <Inline spread="space-between" alignBlock="center">
              <Text color="color.text.subtle">Loading details</Text>
              <Spinner size="medium" />
            </Inline>
          )}
        </Stack>
      </Box>
    );
  }

  return (
    <Box style={cardStyles}>
      <Stack space="space.250">
        <Inline spread="space-between" alignBlock="center">
          <Text size="large" weight="medium">
            {title}
          </Text>
          {isEditing ? (
            <Inline space="space.100">
              <Button appearance="primary" isLoading={isSaving} onClick={() => onSave(draft)}>
                Save
              </Button>
              <Button
                appearance="danger"
                isDisabled={isSaving}
                onClick={() => {
                  setDraft(value);
                  setIsEditing(false);
                }}
              >
                Cancel
              </Button>
            </Inline>
          ) : (
            <Button appearance="subtle" onClick={() => setIsEditing(true)}>
              Edit
            </Button>
          )}
        </Inline>

        <Box style={dividerStyles} />

        {helperMessage ? <Text color="color.text.subtle">{helperMessage}</Text> : null}

        <div
          style={{
            display: 'grid',
            gap: token('space.250'),
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))'
          }}
        >
          {fields.map((field) => {
            const fieldValue = draft[field.key] as EditableFieldValue;

            return (
              <div key={String(field.key)}>
                <Stack space="space.050">
                  <Text color="color.text.subtle" size="small">
                    {field.label}
                  </Text>
                  {isEditing ? (
                    <TextField
                      aria-label={field.label}
                      type={field.type ?? 'text'}
                      value={String(fieldValue ?? '')}
                      onChange={(event) => {
                        const nextRawValue = event.currentTarget.value;
                        setDraft((currentDraft) => {
                          if (!currentDraft) {
                            return currentDraft;
                          }

                          return {
                            ...currentDraft,
                            [field.key]:
                              field.type === 'number' ? Number(nextRawValue || 0) : nextRawValue
                          };
                        });
                      }}
                    />
                  ) : (
                    <Text size="medium">{String(fieldValue || 'Not provided')}</Text>
                  )}
                </Stack>
              </div>
            );
          })}
        </div>

        {'updatedAt' in resolvedValue && 'updatedBy' in resolvedValue ? (
          <Text color="color.text.subtle" size="small">
            Last updated {formatAuditDate(String(resolvedValue.updatedAt))} by{' '}
            {String(resolvedValue.updatedBy || 'system')}
          </Text>
        ) : null}
      </Stack>
    </Box>
  );
}

const isMissingDetailsError = (error: unknown) =>
  error instanceof ApiRequestError && error.status === 400;

const getErrorMessage = (error: unknown) =>
  error instanceof Error ? error.message : 'Unable to load details.';

const pocFields: DetailField<DpPocDetails>[] = [
  { key: 'dpPocFirstName', label: 'First Name' },
  { key: 'dpPocLastName', label: 'Last Name' },
  { key: 'dpPocEmailAddress', label: 'Email Address', type: 'email' },
  { key: 'dpPocMobileNumber', label: 'Phone Number' }
];

const companyFields: DetailField<DpCompanyDetails>[] = [
  { key: 'dpCompanyName', label: 'Company Name' },
  { key: 'dpCompanyAddress', label: 'Address' },
  { key: 'dpCompanyCity', label: 'City' },
  { key: 'dpCompanyState', label: 'State' },
  { key: 'dpCompanyCountry', label: 'Country' },
  { key: 'dpCompanyZipCode', label: 'ZIP Code' }
];

export default function DemandPartnerDetailsPage() {
  const { dpId = '' } = useParams();
  const dispatch = useAppDispatch();
  const demandPartnerProfile = useAppSelector((state) =>
    dpId ? state.partner.demandPartnerProfiles[dpId] : undefined
  );
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const breadcrumbs = (
    <Breadcrumbs>
      <BreadcrumbsItem text="Dashboard" key="Dashboard" onClick={() => navigate('/home')} />
      <BreadcrumbsItem
        text="Demand Partners"
        key="Demand Partners"
        onClick={() => navigate('/demand-partners')}
      />
      <BreadcrumbsItem text={dpId} key={dpId} />
    </Breadcrumbs>
  );

  const pocQuery = useQuery({
    queryKey: ['demandPartnerPocDetails', dpId],
    queryFn: (): Promise<DpPocDetails> => demandPartnerServices.getDpPocDetails(dpId),
    enabled: Boolean(dpId)
  });

  const companyQuery = useQuery({
    queryKey: ['demandPartnerCompanyDetails', dpId],
    queryFn: (): Promise<DpCompanyDetails> => demandPartnerServices.getDpCompanyDetails(dpId),
    enabled: Boolean(dpId)
  });

  const pocMutation = useMutation({
    mutationFn: (payload: DpPocDetails) => demandPartnerServices.updateDpPocDetails(dpId, payload),
    onSuccess: async () => {
      const profile = await demandPartnerServices.getDPprofileDetails(dpId);
      dispatch(setDemandPartnerProfile(profile));
      void queryClient.invalidateQueries({ queryKey: ['demandPartnerPocDetails', dpId] });
    }
  });

  const companyMutation = useMutation({
    mutationFn: (payload: DpCompanyDetails) =>
      demandPartnerServices.updateDpCompanyDetails(dpId, payload),
    onSuccess: async () => {
      const profile = await demandPartnerServices.getDPprofileDetails(dpId);
      dispatch(setDemandPartnerProfile(profile));
      void queryClient.invalidateQueries({ queryKey: ['demandPartnerCompanyDetails', dpId] });
    }
  });

  const subtitle = useMemo(() => {
    if (!demandPartnerProfile) {
      return '';
    }

    return `${demandPartnerProfile.dpName} • ${demandPartnerProfile.dpType} • RTB ${demandPartnerProfile.rtbVersion}`;
  }, [demandPartnerProfile]);

  const pocFallbackValue = useMemo<DpPocDetails | undefined>(() => {
    if (!isMissingDetailsError(pocQuery.error)) {
      return undefined;
    }

    return {
      dpId,
      dpPocFirstName: '',
      dpPocLastName: '',
      dpPocEmailAddress: '',
      dpPocMobileNumber: ''
    };
  }, [dpId, pocQuery.error]);

  const companyFallbackValue = useMemo<DpCompanyDetails | undefined>(() => {
    if (!isMissingDetailsError(companyQuery.error)) {
      return undefined;
    }

    return {
      dpId,
      dpCompanyName: demandPartnerProfile?.dpName ?? '',
      dpCompanyAddress: '',
      dpCompanyCity: '',
      dpCompanyState: '',
      dpCompanyCountry: '',
      dpCompanyZipCode: ''
    };
  }, [companyQuery.error, demandPartnerProfile?.dpName, dpId]);

  const isPageLoading = pocQuery.isLoading || companyQuery.isLoading;

  if (!dpId) {
    return (
      <Stack space="space.300" xcss={{ width: '100%' }}>
        <PageHeader breadcrumbs={breadcrumbs}>Demand Partner Details</PageHeader>
        <Text>Demand partner ID not found.</Text>
      </Stack>
    );
  }

  return (
    <Stack space="space.300" xcss={{ width: '100%' }}>
      <PageHeader breadcrumbs={breadcrumbs}>
        {demandPartnerProfile?.dpName ?? `Demand Partner ${dpId}`}
      </PageHeader>

      <Box
        style={{
          alignItems: 'center',
          background: token('color.background.neutral.subtle'),
          borderRadius: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          padding: token('space.250')
        }}
      >
        <div>
          <Text size="large" weight="bold">
            {dpId}
          </Text>
          <Text color="color.text.subtle">{subtitle || 'Loading partner profile'}</Text>
        </div>
      </Box>

      {isPageLoading ? (
        <Box
          style={{
            alignItems: 'center',
            display: 'flex',
            height: '240px',
            justifyContent: 'center'
          }}
        >
          <Spinner size="large" />
        </Box>
      ) : (
        <Stack space="space.250">
          <DetailSectionCard
            title="Contact Information"
            value={pocQuery.data}
            fallbackValue={pocFallbackValue}
            fields={pocFields}
            isSaving={pocMutation.isPending}
            onSave={(value) => pocMutation.mutate(value)}
            helperMessage={
              pocFallbackValue
                ? 'Contact details are not available yet. Add them below to create the record.'
                : undefined
            }
            errorMessage={
              pocQuery.error && !pocFallbackValue ? getErrorMessage(pocQuery.error) : undefined
            }
          />
          <DetailSectionCard
            title="Company Details"
            value={companyQuery.data}
            fallbackValue={companyFallbackValue}
            fields={companyFields}
            isSaving={companyMutation.isPending}
            onSave={(value) => companyMutation.mutate(value)}
            helperMessage={
              companyFallbackValue
                ? 'Company details are not available yet. Add them below to create the record.'
                : undefined
            }
            errorMessage={
              companyQuery.error && !companyFallbackValue
                ? getErrorMessage(companyQuery.error)
                : undefined
            }
          />
        </Stack>
      )}
    </Stack>
  );
}
